/**
 * CUBICO Slideshow — Custom Element
 * 100% matching cubico-shop.com (Shopline) slideshow behavior.
 * Decompiled from original source + cleaned up.
 */

const SLIDE_TRANSLATE_X = 25;

/* YouTube IFrame API */
const YTPlayers = {};
function onYouTubeIframeAPIReady() { window.YTReady = true; }
(function loadYTAPI() {
  if (window.YT) { window.YTReady = true; return; }
  const s = document.createElement('script');
  s.src = 'https://www.youtube.com/iframe_api';
  s.async = true;
  document.head.appendChild(s);
})();

function playYTVideo(slideEl) {
  const iframe = slideEl.querySelector('iframe[data-video-type="youtube"]');
  if (!iframe) return;
  // Lazy load: set src from data-src
  if (iframe.dataset.src && (!iframe.src || iframe.src === '' || iframe.src === 'about:blank')) {
    iframe.src = iframe.dataset.src;
  }
  // Use YT API to play
  const tryPlay = () => {
    if (YTPlayers[iframe.id]) { YTPlayers[iframe.id].playVideo(); return; }
    if (!window.YT || !window.YTReady) { setTimeout(tryPlay, 300); return; }
    try {
      const p = new YT.Player(iframe.id, {
        events: {
          onReady: (e) => { e.target.mute(); e.target.setLoop(true); YTPlayers[iframe.id] = e.target; e.target.playVideo(); }
        }
      });
    } catch(e) {}
  };
  tryPlay();
}

function pauseYTVideo(slideEl) {
  const iframe = slideEl.querySelector('iframe[data-video-type="youtube"]');
  if (!iframe || !YTPlayers[iframe.id]) return;
  try { YTPlayers[iframe.id].pauseVideo(); } catch(e) {}
}

if (!customElements.get('cubico-slideshow')) {
  class SlideshowComponent extends HTMLElement {
    constructor() {
      super();

      this.isLoading = false;
      this.firstTouchX = null;
      this.lastTouchX = null;
      this.currentPage = 0;
      this.nextPage = 0;
      this.prevPage = 0;
      this.autoPlayInterval = null;

      // Query elements
      this.list = this.querySelector('.slideshow__list, .js-slideshow-list');
      this.slides = this.querySelectorAll('.slideshow__item');
      this.slidesInner = this.querySelectorAll('.slideshow__item-inner');
      this.pageButtons = this.querySelectorAll('.slideshow__page-button');
      this.root = this.closest('.cubico-section-width') || this;
      this.mobileText = this.root.querySelectorAll('.slideshow__text--mobile');

      // Bind methods
      this.setSliderInnerWidth = this.setSliderInnerWidth.bind(this);
      this.setPageValue = this.setPageValue.bind(this);
      this.onNextBtnClick = this.onNextBtnClick.bind(this);
      this.onPrevBtnClick = this.onPrevBtnClick.bind(this);
      this.slideToPage = this.slideToPage.bind(this);
      this.syncMobileText = this.syncMobileText.bind(this);
      this.handleMouseMove = this.handleMouseMove.bind(this);
      this.handleMouseUp = this.handleMouseUp.bind(this);
      this.handleSwipeMove = this.handleSwipeMove.bind(this);
      this.handleSwipeEnd = this.handleSwipeEnd.bind(this);

      // Page button clicks
      this.pageButtons.forEach((btn) => {
        btn.addEventListener('click', this.onPageBtnClick.bind(this));
      });

      // Touch / mouse drag on track
      if (this.slides.length > 1 && this.list) {
        this.list.addEventListener('touchstart', this.handleTouchStart.bind(this));
        this.list.addEventListener('touchmove', this.handleTouchMove.bind(this));
        this.list.addEventListener('touchend', this.handleTouchEnd.bind(this));
        this.list.addEventListener('mousedown', this.handleMouseDown.bind(this));
      }

      // Arrow buttons
      const nextBtn = this.querySelector('.slideshow__next-button');
      const prevBtn = this.querySelector('.slideshow__prev-button');
      if (nextBtn) nextBtn.addEventListener('click', this.onNextBtnClick);
      if (prevBtn) prevBtn.addEventListener('click', this.onPrevBtnClick);

      // Init
      if (window.requestIdleCallback) {
        window.requestIdleCallback(this.resetAutoPlayInterval.bind(this));
      } else {
        setTimeout(this.resetAutoPlayInterval.bind(this), 0);
      }

      this.setPageValue(0);
      this.updateSlidesVisibility(this.currentPage);
      this.syncMobileText(this.currentPage);
      this.setSliderInnerWidth();

      // Resize handler
      window.addEventListener('resize', () => {
        this.setSliderInnerWidth();
      });

      // Prevent swipe from triggering link clicks on text buttons
      this.querySelectorAll('.slideshow__text-button').forEach((btn) => {
        btn.addEventListener('mousedown', (e) => e.stopPropagation());
      });

      // Hover: pause autoplay, leave: resume
      this.addEventListener('mouseenter', () => {
        clearInterval(this.autoPlayInterval);
        // Pause video: replace src with paused version
        try {
          const active = this.querySelector('.slideshow__item--active iframe[data-video-type="youtube"]');
          if (active && active.src && active.src !== 'about:blank') {
            active.dataset.pausedSrc = active.src;
            active.src = active.src.replace('autoplay=1', 'autoplay=0');
          }
        } catch(e) {}
      });
      this.addEventListener('mouseleave', () => {
        this.resetAutoPlayInterval();
        // Resume video
        try {
          const active = this.querySelector('.slideshow__item--active iframe[data-video-type="youtube"]');
          if (active && active.dataset.pausedSrc) {
            active.src = active.dataset.pausedSrc;
            delete active.dataset.pausedSrc;
          }
        } catch(e) {}
      });
    }

    /* ========================
       Inner width (min-width so image doesn't squish)
       ======================== */
    setSliderInnerWidth() {
      const w = this.clientWidth;
      this.slidesInner.forEach((inner) => {
        inner.style.minWidth = w + 'px';
      });
    }

    syncMobileText(index) {
      this.mobileText.forEach((textEl, textIndex) => {
        textEl.classList.remove('slideshow__text--active');

        const hasText = textEl.textContent.trim().length > 0;
        const hasLink = !!textEl.querySelector('a[href]:not([href=""])');
        if (textIndex === index && (hasText || hasLink)) {
          textEl.classList.add('slideshow__text--active');
        }
      });
    }

    /* ========================
       Show slide inner (remove is-hidden)
       ======================== */
    updateSlidesVisibility(index) {
      const inner = this.slides[index]?.querySelector('.slideshow__item-inner');
      if (inner) inner.classList.remove('is-hidden');
    }

    /* ========================
       Page tracking
       ======================== */
    getPageValue(page) {
      return {
        nextPage: page === this.slides.length - 1 ? 0 : page + 1,
        prevPage: page === 0 ? this.slides.length - 1 : page - 1,
      };
    }

    setPageValue(page) {
      const { nextPage, prevPage } = this.getPageValue(page);
      this.currentPage = page;
      this.nextPage = nextPage;
      this.prevPage = prevPage;
    }

    /* ========================
       Autoplay
       ======================== */
    resetAutoPlayInterval() {
      const duration = parseInt(this.dataset.duration);
      clearInterval(this.autoPlayInterval);
      if (duration > 0 && this.slides.length > 1) {
        this.autoPlayInterval = setInterval(this.onNextBtnClick, duration * 1000);
      }
    }

    /* ========================
       Slide transition
       ======================== */
    slideToPage(targetIndex) {
      this.isLoading = true;

      const prevSlide = this.slides[this.currentPage];
      const nextSlide = this.slides[targetIndex];

      if (this.currentPage !== targetIndex) {
        // Update indicator dots
        const activePageBtn = this.querySelector('.slideshow__page-button--active');
        if (activePageBtn) activePageBtn.classList.remove('slideshow__page-button--active');

        const targetPageBtn = this.pageButtons[targetIndex];
        if (targetPageBtn) targetPageBtn.classList.add('slideshow__page-button--active');

        this.syncMobileText(targetIndex);

        // Swap classes
        prevSlide.classList.add('slideshow__item--prev');
        prevSlide.classList.remove('slideshow__item--active');
        nextSlide.classList.add('slideshow__item--active');
      }

      // Clean up after transition
      const onTransitionEnd = () => {
        nextSlide.removeEventListener('transitionend', onTransitionEnd);

        const prevEl = this.querySelector('.slideshow__item--prev');
        if (prevEl) prevEl.classList.remove('slideshow__item--prev');

        this.classList.remove('slideshow--sliding-left', 'slideshow--sliding-right', 'slideshow--swiped');
        this.setPageValue(targetIndex);
        this.resetAutoPlayInterval();
        this.isLoading = false;
        // Video: play active, pause others (non-blocking)
        try {
          this.querySelectorAll('iframe[data-video-type="youtube"]').forEach(iframe => {
            const slide = iframe.closest('.slideshow__item');
            if (slide && slide.classList.contains('slideshow__item--active')) playYTVideo(slide);
            else pauseYTVideo(slide);
          });
        } catch(e) {}
      };

      nextSlide.addEventListener('transitionend', onTransitionEnd);

      // Safety timeout in case transitionend doesn't fire
      setTimeout(() => {
        if (this.isLoading) {
          onTransitionEnd();
        }
      }, 800);
    }

    /* ========================
       Next / Prev
       ======================== */
    onNextBtnClick() {
      if (this.isLoading) return;
      this.classList.add('slideshow--sliding-left');
      this.updateSlidesVisibility(this.nextPage);
      this.slideToPage(this.nextPage);
    }

    onPrevBtnClick() {
      if (this.isLoading) return;
      this.classList.add('slideshow--sliding-right');
      this.updateSlidesVisibility(this.prevPage);
      this.slideToPage(this.prevPage);
    }

    onPageBtnClick(e) {
      const index = parseInt(e.currentTarget.dataset.index);
      if (this.currentPage === index || this.isLoading) return;
      this.classList.add(index > this.currentPage ? 'slideshow--sliding-left' : 'slideshow--sliding-right');
      this.updateSlidesVisibility(index);
      this.slideToPage(index);
    }

    /* ========================
       Swipe (shared logic for touch + mouse drag)
       ======================== */
    handleSwipeMove() {
      if (this.isLoading || this.firstTouchX === null) return;

      this.classList.remove('slideshow--sliding-left', 'slideshow--sliding-right');
      clearInterval(this.autoPlayInterval);
      // Pause all videos during swipe (non-blocking)
      try {
        this.querySelectorAll('iframe[data-video-type="youtube"]').forEach(iframe => {
          pauseYTVideo(iframe.closest('.slideshow__item'));
        });
      } catch(e) {}

      // Reset next/prev slides
      [this.nextPage, this.prevPage].forEach((page) => {
        this.slides[page].classList.remove('slideshow__item--prev');
        this.slides[page].removeAttribute('style');
        this.slidesInner[page].style.removeProperty('transform');
      });

      const diff = this.firstTouchX - this.lastTouchX;
      const translatePercent = (Math.abs(diff) / this.clientWidth) * SLIDE_TRANSLATE_X;

      let targetPage, slideshowClass, currentSlideX, targetSlideX;

      if (diff > 0) {
        // Swiping left → show next
        targetPage = this.nextPage;
        slideshowClass = 'slideshow--sliding-right';
        currentSlideX = -translatePercent;
        targetSlideX = -(translatePercent - SLIDE_TRANSLATE_X);
      } else {
        // Swiping right → show prev
        targetPage = this.prevPage;
        slideshowClass = 'slideshow--sliding-left';
        currentSlideX = translatePercent;
        targetSlideX = translatePercent - SLIDE_TRANSLATE_X;
      }

      this.updateSlidesVisibility(targetPage);
      this.classList.add('slideshow--swiping');
      this.classList.add(slideshowClass);

      this.slides[targetPage].classList.add('slideshow__item--prev');
      this.slides[targetPage].style.width = Math.abs(diff) + 'px';
      this.slides[this.currentPage].style.width = (this.clientWidth - Math.abs(diff)) + 'px';
      this.slidesInner[this.currentPage].style.transform = 'translateX(' + currentSlideX + '%)';
      this.slidesInner[targetPage].style.transform = 'translateX(' + targetSlideX + '%)';
    }

    handleSwipeEnd() {
      if (this.isLoading || this.firstTouchX === null || this.lastTouchX === null) return;

      this.classList.remove('slideshow--swiping');
      this.classList.add('slideshow--swiped');

      // Reset inline styles
      [this.currentPage, this.nextPage, this.prevPage].forEach((page) => {
        this.slides[page].removeAttribute('style');
        this.slidesInner[page].style.removeProperty('transform');
      });

      const diff = this.firstTouchX - this.lastTouchX;

      if (Math.abs(diff) > this.clientWidth / 10) {
        // Swipe threshold met — go to target page
        const targetPage = diff > 0 ? this.nextPage : this.prevPage;
        this.slides[targetPage].classList.remove('slideshow__item--prev');
        this.classList.toggle('slideshow--sliding-right');
        this.classList.toggle('slideshow--sliding-left');
        this.slideToPage(targetPage);
      } else {
        // Swipe too short — snap back
        this.slideToPage(this.currentPage);
      }

      this.firstTouchX = null;
      this.lastTouchX = null;
      this.resetAutoPlayInterval();
    }

    /* ========================
       Touch events
       ======================== */
    handleTouchStart(e) {
      if (this.isLoading) return;
      this.firstTouchX = e.touches[0].clientX;
    }

    handleTouchMove(e) {
      this.lastTouchX = e.touches[0].clientX;
      this.handleSwipeMove();
    }

    handleTouchEnd() {
      this.handleSwipeEnd();
    }

    /* ========================
       Mouse drag events
       ======================== */
    handleMouseDown(e) {
      if (this.isLoading) return;
      document.addEventListener('mousemove', this.handleMouseMove);
      document.addEventListener('mouseup', this.handleMouseUp);
      this.firstTouchX = e.clientX;
    }

    handleMouseMove(e) {
      this.lastTouchX = e.clientX;
      this.handleSwipeMove();
    }

    handleMouseUp() {
      document.removeEventListener('mousemove', this.handleMouseMove);
      document.removeEventListener('mouseup', this.handleMouseUp);
      this.handleSwipeEnd();
    }
  }

  customElements.define('cubico-slideshow', SlideshowComponent);
}
